import { uiSurface } from "shared/ui/uiSurface"
import { Save } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateConfigurationMutation,
  useGetConfigurationsQuery,
  useUpdateConfigurationMutation,
} from "../../apis/appApi"

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"

type DaySchedule = {
  open: string
  close: string
  closed: boolean
}

type WorkingHoursData = Record<DayKey, DaySchedule>

const DAY_KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]

const DAY_I18N_KEYS: Record<DayKey, string> = {
  mon: "configuration.workingHours.monday",
  tue: "configuration.workingHours.tuesday",
  wed: "configuration.workingHours.wednesday",
  thu: "configuration.workingHours.thursday",
  fri: "configuration.workingHours.friday",
  sat: "configuration.workingHours.saturday",
  sun: "configuration.workingHours.sunday",
}

const DEFAULT_WORKING_HOURS: WorkingHoursData = {
  mon: { open: "08:00", close: "17:00", closed: false },
  tue: { open: "08:00", close: "17:00", closed: false },
  wed: { open: "08:00", close: "17:00", closed: false },
  thu: { open: "08:00", close: "17:00", closed: false },
  fri: { open: "08:00", close: "17:00", closed: false },
  sat: { open: "", close: "", closed: true },
  sun: { open: "", close: "", closed: true },
}

const CONFIG_KEY = "WORKING_HOURS"

function parseWorkingHours(value: string | undefined): WorkingHoursData {
  if (!value) return { ...DEFAULT_WORKING_HOURS }
  try {
    const parsed = JSON.parse(value)
    const result: WorkingHoursData = { ...DEFAULT_WORKING_HOURS }
    for (const key of DAY_KEYS) {
      if (parsed[key]) {
        result[key] = {
          open: parsed[key].open ?? "",
          close: parsed[key].close ?? "",
          closed: parsed[key].closed ?? false,
        }
      }
    }
    return result
  } catch {
    return { ...DEFAULT_WORKING_HOURS }
  }
}

export const WorkingHoursTab: FC = () => {
  const { t } = useTranslation()
  const { data: configurations, isLoading } = useGetConfigurationsQuery()
  const [createConfiguration] = useCreateConfigurationMutation()
  const [updateConfiguration] = useUpdateConfigurationMutation()

  const [form, setForm] = useState<WorkingHoursData>(DEFAULT_WORKING_HOURS)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: "success" | "error"
    message: string
  }>({ open: false, severity: "success", message: "" })
  const [saving, setSaving] = useState(false)

  // Find the existing config entry for WORKING_HOURS
  const configEntry = useMemo(() => {
    if (!configurations) return null
    const found = configurations.find((cfg: any) => cfg.name === CONFIG_KEY)
    return found ? { id: found.id, value: found.value ?? "" } : null
  }, [configurations])

  // Load values from config on mount / data change
  const loadedValues = useMemo(
    () => parseWorkingHours(configEntry?.value),
    [configEntry],
  )

  useEffect(() => {
    setForm(loadedValues)
  }, [loadedValues])

  const hasChanges = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(loadedValues),
    [form, loadedValues],
  )

  const setDayField = useCallback(
    (day: DayKey, field: keyof DaySchedule, value: string | boolean) => {
      setForm((prev) => ({
        ...prev,
        [day]: { ...prev[day], [field]: value },
      }))
    },
    [],
  )

  const handleSave = async () => {
    setSaving(true)
    try {
      const jsonValue = JSON.stringify(form)
      if (configEntry) {
        await updateConfiguration({
          id: configEntry.id,
          body: { value: jsonValue },
        })
      } else {
        await createConfiguration({
          name: CONFIG_KEY,
          value: jsonValue,
          enabled: true,
        })
      }
      setSnackbar({
        open: true,
        severity: "success",
        message: t("configuration.workingHours.saved"),
      })
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: t("configuration.workingHours.saveError"),
      })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box
        data-testid="working-hours-loading"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container
      {...uiSurface("UI-DLV2-184")}
      data-testid="working-hours-tab"
      maxWidth="md"
      sx={{ py: 4 }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {t("configuration.workingHours.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("configuration.workingHours.subtitle")}
        </Typography>
      </Box>

      <TableContainer
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          mb: 3,
          "& .MuiTableCell-head": {
            bgcolor: "grey.50",
            fontWeight: 600,
            fontSize: 12,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            py: 1.5,
          },
          "& .MuiTableCell-body": {
            py: 1.5,
          },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 160 }}>
                {t("configuration.workingHours.day")}
              </TableCell>
              <TableCell sx={{ width: 180 }}>
                {t("configuration.workingHours.open")}
              </TableCell>
              <TableCell sx={{ width: 180 }}>
                {t("configuration.workingHours.close")}
              </TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                {t("configuration.workingHours.closed")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DAY_KEYS.map((day) => (
              <TableRow
                key={day}
                data-testid={`working-hours-row-${day}`}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {t(DAY_I18N_KEYS[day])}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    data-testid={`working-hours-open-${day}`}
                    type="time"
                    size="small"
                    value={form[day].open}
                    onChange={(e) => setDayField(day, "open", e.target.value)}
                    disabled={form[day].closed}
                    sx={{
                      width: 150,
                      "& .Mui-disabled": { bgcolor: "grey.100" },
                    }}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    data-testid={`working-hours-close-${day}`}
                    type="time"
                    size="small"
                    value={form[day].close}
                    onChange={(e) => setDayField(day, "close", e.target.value)}
                    disabled={form[day].closed}
                    sx={{
                      width: 150,
                      "& .Mui-disabled": { bgcolor: "grey.100" },
                    }}
                    slotProps={{
                      inputLabel: { shrink: true },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    data-testid={`working-hours-closed-${day}`}
                    checked={form[day].closed}
                    onChange={(e) =>
                      setDayField(day, "closed", e.target.checked)
                    }
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={3}>
        {/* Save Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            data-testid="working-hours-save-btn"
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{ borderRadius: 2, textTransform: "none", px: 4 }}
          >
            {t("save")}
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
